import { toast } from 'react-toastify';

/**
 * If a roll / managed-event / sub returns 409 GAME_BUSY (another request for this
 * same game is already mid-flight on the server), toast the user and swallow.
 * Returns true when handled so the caller can bail without surfacing a raw error.
 *
 * The upstream caller in useGameEngine also turns off auto-manager on error,
 * which is the desired side effect — secondary tabs disable themselves.
 */
export const handleGameBusy = (error) => {
    const { status, data } = error?.response ?? {};
    if (status !== 409 || data?.code !== 'GAME_BUSY') return false;
    toast.info("This game is already processing another request. Auto-manager paused.", { toastId: 'GAME_BUSY' });
    return true;
};

/**
 * If a roll arrives after the game has already been marked final (common race
 * when auto-manager is running in two tabs — the loser fires one more roll
 * before its cached state catches up), swallow it silently. The existing
 * onError path turns off auto-manager; we just don't want the generic red
 * error toast.
 */
export const handleGameAlreadyFinal = (error) => {
    const { status, data } = error?.response ?? {};
    if (status !== 400 || data?.code !== 'GAME_ALREADY_FINAL') return false;
    toast.info('Game is over.', { toastId: 'GAME_ALREADY_FINAL' });
    return true;
};

/**
 * If a startGame call fails with ACTIVE_GAME_EXISTS, toast the user and redirect them
 * to their in-progress game. Returns true when the error was handled (caller should bail).
 *
 * Kept in its own UI-effect module — gameHelper.js is used in non-UI contexts (tests,
 * pure game math) and should stay side-effect-free.
 */
export const handleActiveGameExists = (error, navigate) => {
    const { status, data } = error?.response ?? {};
    if (status !== 409 || data?.code !== 'ACTIVE_GAME_EXISTS' || !data?.gameID) {
        return false;
    }

    const windowMinutes = Number.isFinite(data.windowMinutes) ? data.windowMinutes : null;
    const message = windowMinutes
        ? `You can only have one active game in a ${windowMinutes} minute period. You've been redirected to the active game.`
        : "You already have an active game. Redirecting you to it.";

    // toastId dedupes rapid repeated fires (e.g. auto-retry loops) into a single toast.
    toast.info(message, { toastId: 'ACTIVE_GAME_EXISTS' });

    // `replace: true` so the user can't back-button into the start-game page and retry.
    navigate(`/game/${encodeURIComponent(String(data.gameID))}/play`, { replace: true });
    return true;
};
